/**
 * /projects — Projects admin list (DataGridPro).
 *
 * Default columns: code · name · status · client (customer) · owner ·
 * project manager · contract amount · billing method
 *
 * Hidden by default (column chooser surfaces them):
 *   bill_to (the bill-to-entity field — rare; only complex multi-party
 *            engagements where billing goes to a different entity than
 *            the customer)
 *   currency
 *   start_date, est_completion_date
 *
 * Row click opens an edit drawer with all fields including the rare
 * bill-to picker. + New project button opens a create dialog with
 * client + owner pickers; bill-to is collapsed under a "Complex
 * billing" toggle in the dialog.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import {
  DataGridPro,
  type GridColDef,
} from "@mui/x-data-grid-pro";
import { gridDefaults, FlexGrid } from "@/grids/gridDefaults";

import { api, downloadFile } from "@/lib/api";
import {
  contractsListQueryOptions,
  numberingConfigQueryOptions,
  numberingDimensionsQueryOptions,
  partiesListQueryOptions,
  projectKeys,
  projectsFullListQueryOptions,
  progressBillingsQueryOptions,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { MoneyField } from "@/components/MoneyField";
import { formatMoney, normalizeAmountInput } from "@/lib/money";
import { Link, useNavigate } from "@tanstack/react-router";
import type {
  BillingMethodStr,
  Contract,
  NumberingAllocateResult,
  Party,
  ProgressBillingSummary,
  ProgressBillingStatus,
  ProjectCreatePayload,
  ProjectFull,
  ProjectStatusStr,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/projects/")({
  component: ProjectsPage,
});


const STATUS_BG: Record<ProjectStatusStr, string> = {
  bidding:   "#dbeafe",
  awarded:   "#d1fae5",
  active:    "#dcfce7",
  hold:      "#fef3c7",
  closed:    "#e5e7eb",
  cancelled: "#fee2e2",
  archived:  "#ede9fe",   // historical import (seeds cost history)
};
const STATUS_FG: Record<ProjectStatusStr, string> = {
  bidding:   "#1e40af",
  awarded:   "#065f46",
  active:    "#166534",
  hold:      "#92400e",
  closed:    "#374151",
  cancelled: "#991b1b",
  archived:  "#5b21b6",
};
const ALL_STATUSES: ProjectStatusStr[] = [
  "bidding", "awarded", "active", "hold", "closed", "cancelled", "archived",
];

const BILLING_METHODS: { value: BillingMethodStr; label: string }[] = [
  { value: "lump_sum",            label: "Lump Sum" },
  { value: "aia_progress",        label: "AIA Progress (G702/G703)" },
  { value: "time_and_materials",  label: "Time & Materials" },
  { value: "cost_plus",           label: "Cost Plus" },
  { value: "unit_price",          label: "Unit Price" },
  { value: "fixed_fee",           label: "Fixed Fee" },
];

type EacSummary = {
  project_id: string;
  revised_budget: string;
  actual_cost: string;
  eac: string;
  variance_at_completion: string | null;  // null = no budget data to compare
  pct_spent: string;
  manual_eac: string | null;              // set ⇒ eac is a PM override
};

// ProjectFull rows enriched with EAC so the grid can sort over-budget to top.
type ProjectRow = ProjectFull & {
  _eac: number | null;
  _variance: number | null;
  _pctSpent: number | null;
  _manualEac: boolean;
};

function ProjectsPage() {
  const [statusFilter, setStatusFilter] = useState<ProjectStatusStr | "">("");
  // Historical imports (status=archived) are excluded by the API unless we ask
  // for them explicitly — so filtering to "archived" fetches them in.
  const projsQuery = useQuery(projectsFullListQueryOptions({
    limit: 500,
    status: statusFilter === "archived" ? ["archived"] : undefined,
  }));
  const partiesQuery = useQuery(partiesListQueryOptions());
  const eacQuery = useQuery({
    queryKey: ["projects", "eac-summary"],
    queryFn: () => api("/api/projects/eac-summary") as Promise<EacSummary[]>,
  });
  const eacById = useMemo(() => {
    const m = new Map<string, EacSummary>();
    for (const e of eacQuery.data ?? []) m.set(e.project_id, e);
    return m;
  }, [eacQuery.data]);
  const partyById = useMemo(() => {
    const m = new Map<string, Party>();
    for (const p of partiesQuery.data ?? []) m.set(p.id, p);
    return m;
  }, [partiesQuery.data]);

  const [selected, setSelected] = useState<ProjectFull | null>(null);
  const [creating, setCreating] = useState(false);
  const [fromCustomers, setFromCustomers] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  // Inline EAC edit → manual override on the project. Clearing the cell
  // (empty value) clears the override so the computed cascade returns.
  const saveManualEac = useCallback(
    async (newRow: ProjectRow, oldRow: ProjectRow) => {
      if (newRow._eac === oldRow._eac) return oldRow;
      const manual = newRow._eac == null || Number.isNaN(newRow._eac)
        ? null : newRow._eac;
      await api(`/api/projects/${newRow.id}`, {
        method: "PATCH",
        body: { manual_eac: manual },
      });
      await qc.invalidateQueries({ queryKey: ["projects", "eac-summary"] });
      toast.success(manual == null
        ? "Manual EAC cleared — back to computed"
        : `EAC set to ${manual.toLocaleString(undefined, {
            style: "currency", currency: "USD", maximumFractionDigits: 0,
          })}`);
      return { ...newRow, _manualEac: manual != null };
    },
    [qc, toast],
  );

  const rows = useMemo<ProjectRow[]>(() => {
    const base = projsQuery.data?.items ?? [];
    const filtered = statusFilter ? base.filter((p) => p.status === statusFilter) : base;
    return filtered.map((p) => {
      const e = eacById.get(p.id);
      return {
        ...p,
        _eac: e ? Number(e.eac) : null,
        _variance: e && e.variance_at_completion != null
          ? Number(e.variance_at_completion) : null,
        _pctSpent: e ? Number(e.pct_spent) : null,
        _manualEac: e?.manual_eac != null,
      };
    });
  }, [projsQuery.data?.items, statusFilter, eacById]);

  const partyLabel = (id: string | null): string => {
    if (!id) return "—";
    const p = partyById.get(id);
    return p ? `${p.code} ${p.name}` : id.slice(0, 8);
  };

  const fmtMoney0 = (v: number) =>
    v.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  const columns = useMemo<GridColDef<ProjectRow>[]>(
    () => [
      {
        field: "code", headerName: "Code", width: 130,
        renderCell: (p) => (
          <span style={{ fontFamily: "monospace", fontSize: 12 }}>
            {p.row.code}
          </span>
        ),
      },
      { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
      {
        field: "status", headerName: "Status", width: 110,
        renderCell: (p) => (
          <Chip size="small" label={p.row.status}
            sx={{
              fontSize: 10, height: 20,
              bgcolor: STATUS_BG[p.row.status],
              color: STATUS_FG[p.row.status],
              fontWeight: 600,
            }} />
        ),
      },
      {
        field: "customer_id", headerName: "Client", width: 180,
        renderCell: (p) => (
          <span className="text-xs">{partyLabel(p.row.customer_id)}</span>
        ),
      },
      {
        field: "owner_party_id", headerName: "Owner", width: 180,
        renderCell: (p) =>
          p.row.owner_party_id ? (
            <span className="text-xs">{partyLabel(p.row.owner_party_id)}</span>
          ) : (
            <span className="text-xs text-slate-400 italic dark:text-slate-500">
              (same as client)
            </span>
          ),
      },
      // Bill-to is hidden by default — column chooser opt-in
      {
        field: "bill_to_party_id", headerName: "Bill to", width: 180,
        renderCell: (p) =>
          p.row.bill_to_party_id ? (
            <span className="text-xs">{partyLabel(p.row.bill_to_party_id)}</span>
          ) : (
            <span className="text-xs text-slate-400 italic dark:text-slate-500">
              (client)
            </span>
          ),
      },
      {
        field: "billing_method", headerName: "Billing", width: 170,
        renderCell: (p) => (
          <span className="text-xs text-slate-600 dark:text-slate-300">
            {BILLING_METHODS.find((b) => b.value === p.row.billing_method)?.label
              ?? p.row.billing_method}
          </span>
        ),
      },
      {
        field: "original_contract_amount", headerName: "Contract", width: 140,
        align: "right", headerAlign: "right",
        renderCell: (p) => (
          <span className="font-mono text-xs">
            {formatMoney(p.row.original_contract_amount, p.row.currency_code)}
          </span>
        ),
      },
      // ── EAC at-a-glance (forecast-aware projected final cost) ──
      {
        field: "_eac", headerName: "EAC", width: 130,
        align: "right", headerAlign: "right",
        type: "number", editable: true,
        description:
          "Estimate at Completion — the projected final cost of the job. "
          + "Double-click to set a manual EAC; clear it to revert to the computed value.",
        renderCell: (p) =>
          p.row._eac == null
            ? <span className="text-xs text-slate-300">—</span>
            : (
              <span className="font-mono text-xs">
                {fmtMoney0(p.row._eac)}
                {p.row._manualEac && (
                  <span className="ml-1 text-[9px] text-slate-400 align-super" title="Manually set EAC">
                    ✎
                  </span>
                )}
              </span>
            ),
      },
      {
        field: "_variance", headerName: "Over / Under", width: 140,
        align: "right", headerAlign: "right",
        description: "Revised budget − EAC. Red = projected over budget.",
        renderCell: (p) => {
          if (p.row._variance == null) return <span className="text-xs text-slate-300">—</span>;
          const v = p.row._variance;
          const over = v < 0;
          return (
            <span className={`font-mono text-xs font-semibold ${over ? "text-red-600" : "text-emerald-700"}`}>
              {over ? "" : "+"}{fmtMoney0(v)}
            </span>
          );
        },
      },
      {
        field: "_pctSpent", headerName: "Health", width: 110,
        align: "center", headerAlign: "center",
        description: "Green = on/under budget · Amber = >90% of budget spent · Red = projected over budget",
        renderCell: (p) => {
          if (p.row._variance == null) return <span className="text-xs text-slate-300">—</span>;
          const over = p.row._variance < 0;
          const tight = !over && (p.row._pctSpent ?? 0) > 90;
          const color = over ? "#ef4444" : tight ? "#f59e0b" : "#22c55e";
          const label = over ? "Over budget" : tight ? "Watch" : "On budget";
          return (
            <span className="inline-flex items-center gap-1.5 text-xs">
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: color, display: "inline-block" }} />
              {label}
            </span>
          );
        },
      },
      { field: "currency_code", headerName: "Curr", width: 60 },
      { field: "start_date", headerName: "Start", width: 105 },
      { field: "estimated_completion_date", headerName: "Est. Done", width: 105 },
      {
        field: "_actions", headerName: "", width: 52,
        sortable: false, filterable: false, disableColumnMenu: true,
        align: "center",
        renderCell: (p) => (
          <IconButton size="small" title="Edit project details"
            onClick={() => setSelected(p.row as ProjectFull)}
            sx={{ fontSize: 14 }}>
            ✎
          </IconButton>
        ),
      },
    ],
    [partyById],
  );

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Projects</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            All projects in the current entity
          </span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 px-6 py-2 flex items-center gap-3 dark:bg-gray-900 dark:border-gray-800">
        <Button size="small" variant="contained" onClick={() => setCreating(true)}>
          + New project
        </Button>
        <Button size="small" variant="outlined" onClick={() => setFromCustomers(true)}>
          Make projects from customers…
        </Button>
        <TextField select size="small" label="Status" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ProjectStatusStr | "")}
          sx={{ minWidth: 140 }}>
          <MenuItem value="">All</MenuItem>
          {ALL_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
        <div className="flex-1" />
        <span className="text-xs text-slate-500 dark:text-slate-400">{rows.length} projects</span>
      </div>

      <div className="flex-1 min-h-0 px-6 py-3">
        <FlexGrid>
          <DataGridPro
            {...gridDefaults}
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            loading={projsQuery.isLoading}
            density="compact"
            initialState={{
              sorting: { sortModel: [{ field: "code", sort: "asc" }] },
              columns: {
                // Bill-to + currency + dates hidden by default; user can
                // surface them via the columns toolbar button.
                columnVisibilityModel: {
                  bill_to_party_id: false,
                  currency_code: false,
                  start_date: false,
                  estimated_completion_date: false,
                },
              },
            }}
            // Cell-level routing: EAC keeps inline editing and the ✎
            // column opens the edit drawer; everywhere else opens the
            // project workspace (Budgets, Phases, COs, Forecast, WIP).
            onCellClick={(p) => {
              if (p.field === "_eac" || p.field === "_actions") return;
              navigate({
                to: "/projects/$projectId",
                params: { projectId: p.id as string },
              });
            }}
            processRowUpdate={saveManualEac}
            onProcessRowUpdateError={(err) =>
              toast.error(`Couldn't save EAC: ${err instanceof Error ? err.message : err}`)}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </FlexGrid>
      </div>

      <Drawer anchor="right" open={selected !== null}
        onClose={() => setSelected(null)}
        PaperProps={{ sx: { width: 520 } }}>
        {selected && (
          <ProjectEditDrawer project={selected}
            parties={partiesQuery.data ?? []}
            onClose={() => setSelected(null)} />
        )}
      </Drawer>

      {creating && (
        <ProjectCreateDialog parties={partiesQuery.data ?? []}
          onClose={() => setCreating(false)} />
      )}
      {fromCustomers && <ProjectsFromCustomersDialog onClose={() => setFromCustomers(false)} />}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Projects from customers (FIX-LOG 114): QuickBooks often keeps each job
// as a customer. The person picks which customers are jobs; each becomes
// a project linked to that customer. Nothing is chosen for them.
// ──────────────────────────────────────────────────────────────
type CustomerCandidate = {
  customer_id: string; name: string; code: string | null; is_active: boolean;
  imported_by: string[]; lines: number; first_posting: string | null;
  last_posting: string | null; revenue: string;
};
type FromCustomersPlanRow = {
  customer_id: string; name: string | null; code?: string | null;
  code_source?: string | null; problem?: string;
};
type FromCustomersPlan = {
  status: ProjectStatusStr; billing_method: BillingMethodStr; code_from: "customer" | "numbering"; numbering: string | null;
  rows: FromCustomersPlanRow[]; count: number; ready: number;
  problems: FromCustomersPlanRow[]; plan_token: string;
};

function ProjectsFromCustomersDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<ProjectStatusStr | "">("");
  const [billing, setBilling] = useState<BillingMethodStr | "">("");
  const [codeFrom, setCodeFrom] = useState<"customer" | "numbering" | "">("");
  const [codes, setCodes] = useState<Record<string, string>>({});
  const [plan, setPlan] = useState<FromCustomersPlan | null>(null);

  const candidatesQuery = useQuery({
    queryKey: [...projectKeys.all, "from-customers", search],
    queryFn: () =>
      api<{ customers: CustomerCandidate[]; shown: number; without_project: number }>(
        `/api/projects/from-customers/candidates${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ""}`,
      ),
  });
  const shown = candidatesQuery.data?.customers ?? [];

  const body = () => ({
    customer_ids: [...picked], status, billing_method: billing, code_from: codeFrom,
    codes: Object.fromEntries(Object.entries(codes).filter(([id, v]) => picked.has(id) && v.trim())),
  });
  const previewMutation = useMutation({
    mutationFn: () => api<FromCustomersPlan>("/api/projects/from-customers/preview", {
      method: "POST", body: JSON.stringify(body()),
    }),
    onSuccess: setPlan,
    onError: (e: Error) => { setPlan(null); toast.error(e.message); },
  });
  const applyMutation = useMutation({
    mutationFn: () => api<{ made_count: number }>("/api/projects/from-customers", {
      method: "POST", body: JSON.stringify({ ...body(), plan_token: plan!.plan_token }),
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: projectKeys.all });
      toast.success(`${r.made_count} project${r.made_count === 1 ? "" : "s"} made from customers`);
      onClose();
      navigate({ to: "/projects" });
    },
    onError: (e: Error) => { toast.error(e.message); previewMutation.mutate(); },
  });

  const toggle = (id: string) => {
    setPlan(null);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allShownPicked = shown.length > 0 && shown.every((c) => picked.has(c.customer_id));
  // Name what still blocks the preview, so a disabled button is never unexplained.
  const missing = [
    picked.size === 0 ? "tick at least one customer" : null,
    status === "" ? "choose a project status" : null,
    billing === "" ? "choose a billing method" : null,
    codeFrom === "" ? "choose where project codes come from" : null,
  ].filter((m): m is string => m !== null);
  const canPreview = missing.length === 0 && !previewMutation.isPending;
  const canApply = !!plan && plan.problems.length === 0 && plan.ready > 0 && !applyMutation.isPending;
  const fmt = (v: string) => formatMoney(Number(v));

  return (
    <Dialog open onClose={applyMutation.isPending ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Make projects from customers</DialogTitle>
      <DialogContent dividers>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
          QuickBooks often keeps each job as a customer. Tick the customers that are jobs: each becomes a
          project linked to that customer, so its history stays with it and new bills and time can be coded
          to the job. Nothing changes for customers you leave unticked, and posted history is not rewritten.
        </p>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <TextField size="small" label="Search customers" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
          <TextField select size="small" label="Project status" value={status} required
            onChange={(e) => { setStatus(e.target.value as ProjectStatusStr); setPlan(null); }}
            sx={{ minWidth: 170 }}>
            {ALL_STATUSES.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Billing method" value={billing} required
            onChange={(e) => { setBilling(e.target.value as BillingMethodStr); setPlan(null); }}
            sx={{ minWidth: 190 }}>
            {BILLING_METHODS.map((b) => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Project codes from" value={codeFrom} required
            onChange={(e) => { setCodeFrom(e.target.value as "customer" | "numbering"); setPlan(null); }}
            sx={{ minWidth: 260 }}>
            <MenuItem value="customer">The customer (its code, or its QuickBooks name)</MenuItem>
            <MenuItem value="numbering">The company's project numbering</MenuItem>
          </TextField>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {picked.size} chosen · {candidatesQuery.data?.without_project ?? "…"} customers have no project
          </span>
        </Stack>
        <div className="max-h-96 overflow-auto border border-slate-200 dark:border-gray-800 rounded">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-gray-800">
              <tr className="text-left text-slate-500">
                <th className="px-2 py-1">
                  <Checkbox size="small" checked={allShownPicked}
                    inputProps={{ "aria-label": "Choose every customer shown" }}
                    onChange={() => {
                      setPlan(null);
                      setPicked((prev) => {
                        const next = new Set(prev);
                        for (const c of shown) {
                          if (allShownPicked) next.delete(c.customer_id); else next.add(c.customer_id);
                        }
                        return next;
                      });
                    }} />
                </th>
                <th className="px-2 py-1">Customer</th>
                <th className="px-2 py-1">From</th>
                <th className="px-2 py-1">Postings</th>
                <th className="px-2 py-1">First – last</th>
                <th className="px-2 py-1 text-right">Revenue</th>
                <th className="px-2 py-1">Project code (optional)</th>
              </tr>
            </thead>
            <tbody>
              {candidatesQuery.isLoading && (
                <tr><td colSpan={7} className="px-2 py-3 text-slate-500">Loading customers…</td></tr>
              )}
              {!candidatesQuery.isLoading && shown.length === 0 && (
                <tr><td colSpan={7} className="px-2 py-3 text-slate-500">
                  {search ? "No customer without a project matches that search." : "Every customer already has a project."}
                </td></tr>
              )}
              {shown.map((c) => (
                <tr key={c.customer_id} className="border-t border-slate-100 dark:border-gray-800">
                  <td className="px-2 py-1">
                    <Checkbox size="small" checked={picked.has(c.customer_id)}
                      inputProps={{ "aria-label": `Make a project from ${c.name}` }}
                      onChange={() => toggle(c.customer_id)} />
                  </td>
                  <td className="px-2 py-1">
                    {c.name}{c.code ? <span className="text-slate-400 ml-1">{c.code}</span> : null}
                    {!c.is_active && <span className="ml-1 text-amber-700">(inactive)</span>}
                  </td>
                  <td className="px-2 py-1">{c.imported_by.length ? "Import" : "Ledger Core"}</td>
                  <td className="px-2 py-1">{c.lines}</td>
                  <td className="px-2 py-1 whitespace-nowrap">
                    {c.first_posting ? `${c.first_posting} – ${c.last_posting}` : "—"}
                  </td>
                  <td className="px-2 py-1 text-right">{fmt(c.revenue)}</td>
                  <td className="px-2 py-1">
                    <TextField size="small" variant="standard" value={codes[c.customer_id] ?? ""}
                      placeholder="from the choice above"
                      inputProps={{ maxLength: 40, "aria-label": `Project code for ${c.name}` }}
                      onChange={(e) => { setPlan(null); setCodes((prev) => ({ ...prev, [c.customer_id]: e.target.value })); }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {plan && (
          <div className="mt-3 text-sm space-y-1">
            <p className="text-slate-700 dark:text-slate-200">
              {plan.ready} of {plan.count} project{plan.count === 1 ? "" : "s"} ready to make, status {plan.status}
              {plan.code_from === "numbering" && plan.numbering ? `, numbered by ${plan.numbering}` : ""}.
            </p>
            <ul className="text-xs text-slate-600 dark:text-slate-300 max-h-40 overflow-auto">
              {plan.rows.map((r) => (
                <li key={r.customer_id} className={r.problem ? "text-red-600" : ""}>
                  {r.name ?? r.customer_id}: {r.problem
                    ? r.problem
                    : r.code ? `code ${r.code}` : "code from the numbering template"}
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {missing.length > 0 && (
          <span className="mr-auto pl-4 text-xs text-slate-600 dark:text-slate-300" role="status">
            To preview: {missing.join(", ")}.
          </span>
        )}
        {plan && plan.problems.length > 0 && (
          <span className="mr-auto pl-4 text-xs text-red-600" role="status">
            Fix the rows marked in red (type a code or untick them), then preview again.
          </span>
        )}
        <Button onClick={onClose} disabled={applyMutation.isPending}>Cancel</Button>
        <Button variant="outlined" disabled={!canPreview} onClick={() => previewMutation.mutate()}>
          {previewMutation.isPending ? "Checking…" : "Preview"}
        </Button>
        <Button variant="contained" disabled={!canApply} onClick={() => applyMutation.mutate()}>
          {applyMutation.isPending ? "Making…" : plan ? `Make ${plan.ready} project${plan.ready === 1 ? "" : "s"}` : "Make projects"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────
// Edit drawer
// ──────────────────────────────────────────────────────────────
function ProjectEditDrawer({
  project, parties, onClose,
}: {
  project: ProjectFull;
  parties: Party[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  // Pull this customer's contracts so we can pick the master contract
  // from the same customer (typical case). When customer_id is unset
  // (rare) we fall back to ALL contracts.
  const customerContractsQuery = useQuery({
    ...contractsListQueryOptions(project.customer_id ?? undefined),
    enabled: project.customer_id !== null,
  });
  const allContractsQuery = useQuery({
    ...contractsListQueryOptions(),
    enabled: project.customer_id === null,
  });
  const candidateContracts = (project.customer_id !== null
    ? customerContractsQuery.data
    : allContractsQuery.data) ?? [];

  // Linked contracts = contracts whose project_id == this project
  // (the N:1 case — multiple amendments / addenda pointing here).
  const linkedContracts = candidateContracts.filter(
    (c) => c.project_id === project.id,
  );

  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? "");
  const [status, setStatus] = useState<ProjectStatusStr>(project.status);
  const [billingMethod, setBillingMethod] = useState<BillingMethodStr>(project.billing_method);
  const [contract, setContract] = useState(() =>
    normalizeAmountInput(project.original_contract_amount),
  );
  const [retentionPct, setRetentionPct] = useState(project.retention_percent);
  const [customerId, setCustomerId] = useState(project.customer_id ?? "");
  const [masterContractId, setMasterContractId] = useState(
    project.master_contract_id ?? "",
  );
  const [ownerId, setOwnerId] = useState(project.owner_party_id ?? "");
  const [billToId, setBillToId] = useState(project.bill_to_party_id ?? "");
  // Show bill-to ONLY when project already has one set — keeps the
  // common-case dialog quiet. Toggle reveals it for new setups.
  const [showBillTo, setShowBillTo] = useState(project.bill_to_party_id !== null);

  const saveMut = useMutation({
    mutationFn: () =>
      api(`/api/projects/${project.id}`, {
        method: "PATCH",
        body: {
          name, description: description || null,
          status, billing_method: billingMethod,
          original_contract_amount: contract,
          retention_percent: retentionPct,
          customer_id: customerId || null,
          master_contract_id: masterContractId || null,
          owner_party_id: ownerId || null,
          bill_to_party_id: showBillTo ? (billToId || null) : null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.all });
      toast.success("Project updated");
      onClose();
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 dark:border-gray-800">
        <span className="font-mono text-sm font-semibold">{project.code}</span>
        <Chip size="small" label={status}
          sx={{
            fontSize: 10, height: 20,
            bgcolor: STATUS_BG[status],
            color: STATUS_FG[status],
            fontWeight: 600,
          }} />
        <div className="flex-1" />
        <Link to="/projects/$projectId" params={{ projectId: project.id }}
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
          onClick={onClose}>
          Open project →
        </Link>
        <IconButton size="small" onClick={onClose}>✕</IconButton>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-3">
        <TextField label="Name" size="small" fullWidth
          value={name} onChange={(e) => setName(e.target.value)} />
        <TextField label="Description" size="small" fullWidth multiline minRows={2}
          value={description} onChange={(e) => setDescription(e.target.value)} />

        <div className="grid grid-cols-2 gap-3">
          <TextField select label="Status" size="small" value={status}
            onChange={(e) => setStatus(e.target.value as ProjectStatusStr)}>
            {ALL_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Billing method" size="small" value={billingMethod}
            onChange={(e) => setBillingMethod(e.target.value as BillingMethodStr)}>
            {BILLING_METHODS.map((b) => (
              <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
            ))}
          </TextField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <MoneyField
            label="Contract amount"
            size="small"
            currency={project.currency_code}
            value={contract}
            onChange={setContract}
          />
          <TextField label="Retention %" size="small" type="number"
            value={retentionPct} onChange={(e) => setRetentionPct(e.target.value)}
            inputProps={{ step: "0.5", min: 0, max: 100 }} />
        </div>

        <div className="border-t border-slate-200 pt-3 mt-3 dark:border-gray-800">
          <div className="text-xs font-semibold text-slate-700 mb-2 dark:text-slate-300">Parties</div>
          <PartyPicker label="Client (customer)" value={customerId}
            onChange={setCustomerId} parties={parties} filter={["customer"]} />
          <PartyPicker label="Owner" value={ownerId} onChange={setOwnerId}
            parties={parties}
            helperText='Building / property owner. Leave blank to mean "same as client".' />

          {/* Rare: bill-to */}
          <label className="flex items-center gap-2 text-xs text-slate-600 mt-3 cursor-pointer dark:text-slate-300">
            <Switch size="small" checked={showBillTo}
              onChange={(_, v) => setShowBillTo(v)} />
            Complex billing — bill to a different party than the client
          </label>
          {showBillTo && (
            <PartyPicker label="Bill to" value={billToId} onChange={setBillToId}
              parties={parties}
              helperText="Used when invoices should address a different entity (parent company, JV, billing-LLC). Leave blank to fall back to client." />
          )}
        </div>

        {/* Contracts */}
        <div className="border-t border-slate-200 pt-3 mt-3 dark:border-gray-800">
          <div className="text-xs font-semibold text-slate-700 mb-2 dark:text-slate-300">Contracts</div>

          {/* Master contract picker (1:N case) */}
          <TextField select size="small" fullWidth
            label="Master contract"
            value={masterContractId}
            onChange={(e) => setMasterContractId(e.target.value)}
            helperText="When this project's terms come from a master service agreement that covers multiple projects."
            sx={{ mb: 2 }}>
            <MenuItem value="">— none —</MenuItem>
            {candidateContracts.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                <span className="font-mono text-xs mr-2">{c.contract_no}</span>
                {c.title}
              </MenuItem>
            ))}
          </TextField>

          {/* Linked contracts (N:1 case) — read-only list */}
          {linkedContracts.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 dark:text-slate-400">
                Specific-project contracts ({linkedContracts.length})
              </div>
              <div className="space-y-1">
                {linkedContracts.map((c) => (
                  <Link key={c.id}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    to={"/contracts/$contractId" as any}
                    params={{ contractId: c.id }}
                    onClick={onClose}
                    className="flex items-center gap-2 text-xs hover:bg-slate-50 px-2 py-1 rounded dark:hover:bg-gray-800">
                    <span className="font-mono">{c.contract_no}</span>
                    <span className="text-slate-700 truncate dark:text-slate-300">{c.title}</span>
                    <span className="text-[10px] text-slate-400 ml-auto dark:text-slate-500">{c.status}</span>
                  </Link>
                ))}
              </div>
              <div className="text-[10px] text-slate-400 italic mt-1 dark:text-slate-500">
                These contracts have project_id = this project (set via the contract page).
              </div>
            </div>
          )}
        </div>

        {/* Progress Billings (AIA G702/G703) */}
        {project.billing_method === "aia_progress" && (
          <ProgressBillingsSection projectId={project.id} />
        )}
      </div>

      <div className="border-t border-slate-200 p-3 dark:border-gray-800">
        <Button fullWidth variant="contained"
          disabled={!name || saveMut.isPending} onClick={() => saveMut.mutate()}>
          {saveMut.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Progress Billings sub-section (shown inside project drawer
// when billing_method === "aia_progress")
// ──────────────────────────────────────────────────────────────
const PB_STATUS_COLORS: Record<ProgressBillingStatus, { bg: string; fg: string }> = {
  draft:     { bg: "#e2e8f0", fg: "#475569" },
  submitted: { bg: "#dbeafe", fg: "#1e40af" },
  approved:  { bg: "#d1fae5", fg: "#065f46" },
  invoiced:  { bg: "#dcfce7", fg: "#166534" },
  paid:      { bg: "#f0fdf4", fg: "#15803d" },
  rejected:  { bg: "#fee2e2", fg: "#991b1b" },
  reversed:  { bg: "#f1f5f9", fg: "#64748b" },
};

function ProgressBillingsSection({ projectId }: { projectId: string }) {
  const pbQuery = useQuery(progressBillingsQueryOptions(projectId));

  const handleDownload = useCallback((pb: ProgressBillingSummary) => {
    downloadFile(
      `/api/projects/progress-billings/${pb.id}/pdf`,
      `G702-G703-App${pb.application_no}-${pb.billing_date}.pdf`,
    ).catch((err: Error) => alert(err.message));
  }, []);

  if (pbQuery.isLoading) return null;
  const pbs = pbQuery.data?.items ?? [];
  const totalBillings = pbQuery.data?.total ?? 0;

  return (
    <div className="border-t border-slate-200 pt-3 mt-3 dark:border-gray-800">
      <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-2 dark:text-slate-300">
        Progress Billings
        <span className="text-slate-400 font-normal dark:text-slate-500">(AIA G702/G703)</span>
        <span className="ml-auto text-[10px] text-slate-400 dark:text-slate-500">
          {totalBillings} application{totalBillings !== 1 ? "s" : ""}
        </span>
      </div>

      {pbs.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic dark:text-slate-500">
          No progress billing applications yet.
        </p>
      ) : (
        <div className="space-y-1">
          {pbs.map((pb) => {
            const col = PB_STATUS_COLORS[pb.status] ?? PB_STATUS_COLORS.draft;
            return (
              <div
                key={pb.id}
                className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1.5 dark:bg-gray-950"
              >
                {/* App # */}
                <span className="font-mono font-semibold text-slate-700 w-6 text-center dark:text-slate-300">
                  #{pb.application_no}
                </span>

                {/* Period + due */}
                <span className="text-slate-500 flex-1 truncate dark:text-slate-400">
                  thru {pb.period_to}
                  {" · "}
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    ${Number(pb.current_due).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </span>

                {/* Status badge */}
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                  style={{ background: col.bg, color: col.fg }}
                >
                  {pb.status}
                </span>

                {/* PDF download */}
                <button
                  title="Download G702/G703 PDF"
                  className="text-blue-600 hover:text-blue-800 text-[11px] font-medium ml-1 shrink-0"
                  onClick={() => handleDownload(pb)}
                >
                  ↓ PDF
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Create dialog
// ──────────────────────────────────────────────────────────────
function ProjectCreateDialog({
  parties, onClose,
}: {
  parties: Party[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const cfgQuery = useQuery(numberingConfigQueryOptions());
  const dimsQuery = useQuery(numberingDimensionsQueryOptions(false));
  const dims = dimsQuery.data ?? [];
  const cfg = cfgQuery.data ?? null;
  const numberingActive = Boolean(cfg && cfg.format_template);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState<ProjectStatusStr>("bidding");
  const [billingMethod, setBillingMethod] = useState<BillingMethodStr>("aia_progress");
  const [contract, setContract] = useState("0");
  const [customerId, setCustomerId] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [billToId, setBillToId] = useState("");
  const [showBillTo, setShowBillTo] = useState(false);
  const [currency, setCurrency] = useState("USD");
  // Dimension picks: { dimension.key: dimension_value.id }
  const [dimensionValues, setDimensionValues] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState(false);

  // Tokens the active template references — drives which dimension
  // pickers actually need a value before Generate works
  const referencedTokens = useMemo(() => {
    if (!cfg) return new Set<string>();
    const out = new Set<string>();
    for (const m of cfg.format_template.matchAll(/\{([A-Z][A-Z0-9_]*)/g)) {
      out.add(m[1]);
    }
    return out;
  }, [cfg]);
  // Dimensions that drive the template (prefix or sequence-affecting)
  const requiredDims = dims.filter((d) => referencedTokens.has(d.token));

  const generateCode = async () => {
    setGenerating(true);
    try {
      const r = await api<NumberingAllocateResult>(
        "/api/numbering/allocate",
        {
          method: "POST",
          body: { preview: true, dimension_values: dimensionValues },
        },
      );
      setCode(r.code);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  const createMut = useMutation({
    mutationFn: () => {
      const payload: ProjectCreatePayload = {
        code: code.trim(),
        name: name.trim(),
        status,
        billing_method: billingMethod,
        original_contract_amount: contract,
        customer_id: customerId || null,
        owner_party_id: ownerId || null,
        bill_to_party_id: showBillTo ? (billToId || null) : null,
        currency_code: currency.toUpperCase(),
        dimension_values: dimensionValues,
      };
      return api<ProjectFull>("/api/projects", { method: "POST", body: payload });
    },
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: projectKeys.all });
      toast.success(`Project ${p.code} created — set up its budget`);
      onClose();
      // A new job's next step is always the budget (phases × cost types ×
      // cost codes drive billing) — land the user there, not back on the list.
      navigate({
        to: "/projects/$projectId",
        params: { projectId: p.id },
        search: { tab: "budgets" } as never,
      });
    },
    onError: (err: Error) => toast.error(`Create failed: ${err.message}`),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New project</DialogTitle>
      <DialogContent dividers>
        {/* Use MUI's layout spacing here because its outlined labels float
            outside the field box. Utility child-spacing was not reliably
            applied inside this portal, allowing adjacent labels to overlap. */}
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* Dimension pickers (only shown when referenced by template) */}
          {requiredDims.length > 0 && (
            <div className="border border-blue-200 bg-blue-50 rounded p-3 space-y-2">
              <div className="text-xs font-semibold text-blue-800">
                Project tags
                <span className="text-[10px] text-blue-700 ml-2 font-normal">
                  Drive the auto-generated project number
                </span>
              </div>
              {requiredDims.map((d) => (
                <TextField key={d.id} select size="small" label={d.label}
                  value={dimensionValues[d.key] ?? ""}
                  onChange={(e) => setDimensionValues((prev) => ({
                    ...prev,
                    [d.key]: e.target.value,
                  }))}
                  fullWidth>
                  <MenuItem value="">— pick —</MenuItem>
                  {d.values.filter((v) => v.is_active).map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      <span className="font-mono text-xs mr-2">{v.code}</span>
                      {v.label}
                    </MenuItem>
                  ))}
                </TextField>
              ))}
            </div>
          )}

          {/* Code field with Generate button when numbering is configured */}
          <div className="grid grid-cols-12 gap-3">
            <TextField label="Code" size="small" value={code}
              onChange={(e) => setCode(e.target.value)}
              sx={{ gridColumn: "span 7" }}
              helperText={
                numberingActive
                  ? `Template: ${cfg!.format_template} — click Generate to allocate`
                  : "No numbering template set; type the code manually"
              } />
            {numberingActive && (
              <Button variant="outlined" size="small"
                sx={{ gridColumn: "span 3", height: 40 }}
                disabled={generating || requiredDims.some(
                  (d) => !dimensionValues[d.key],
                )}
                onClick={generateCode}>
                {generating ? "…" : "✨ Generate"}
              </Button>
            )}
            <TextField label="Currency" size="small" value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
              inputProps={{ maxLength: 3 }} sx={{ gridColumn: "span 2" }}
              helperText="ISO 4217" />
          </div>
          <TextField label="Name" size="small" fullWidth value={name}
            onChange={(e) => setName(e.target.value)} />

          <div className="grid grid-cols-2 gap-3">
            <TextField select label="Status" size="small" value={status}
              onChange={(e) => setStatus(e.target.value as ProjectStatusStr)}>
              {ALL_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Billing method" size="small" value={billingMethod}
              onChange={(e) => setBillingMethod(e.target.value as BillingMethodStr)}>
              {BILLING_METHODS.map((b) => (
                <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
              ))}
            </TextField>
          </div>

          <MoneyField
            label="Contract amount"
            size="small"
            fullWidth
            currency={currency}
            value={contract}
            onChange={setContract}
          />

          <div className="border-t border-slate-200 pt-3 dark:border-gray-800">
            <div className="text-xs font-semibold text-slate-700 mb-2 dark:text-slate-300">Parties</div>
            <PartyPicker label="Client (customer)" value={customerId}
              onChange={setCustomerId} parties={parties} filter={["customer"]} />
            <PartyPicker label="Owner" value={ownerId} onChange={setOwnerId}
              parties={parties}
              helperText='Building / property owner. Optional; leave blank if same as client.' />

            <label className="flex items-center gap-2 text-xs text-slate-600 mt-3 cursor-pointer dark:text-slate-300">
              <Switch size="small" checked={showBillTo}
                onChange={(_, v) => setShowBillTo(v)} />
              Complex billing — bill to a different party than the client
            </label>
            {showBillTo && (
              <PartyPicker label="Bill to" value={billToId} onChange={setBillToId}
                parties={parties}
                helperText="Used when invoices address a different entity than the client." />
            )}
          </div>

          {!numberingActive && (
            <div className="text-[11px] text-slate-500 mt-2 dark:text-slate-400">
              Tip: configure a numbering template in{" "}
              <a className="text-blue-700 hover:underline"
                 href="/settings/numbering">/settings/numbering</a>{" "}
              to auto-generate project codes. Most firms use{" "}
              <code className="font-mono">{"{COMPANY}-{YY}-{SEQ:4}"}</code> or{" "}
              <code className="font-mono">{"{OFFICE}-{YEAR}-{SEQ:3}"}</code>.
            </div>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained"
          disabled={!code || !name || !currency || createMut.isPending}
          onClick={() => createMut.mutate()}>
          {createMut.isPending ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────
// Reusable party picker — filterable by party_type
// ──────────────────────────────────────────────────────────────
function PartyPicker({
  label, value, onChange, parties, filter, helperText,
}: {
  label: string;
  value: string;
  onChange: (id: string) => void;
  parties: Party[];
  filter?: ("customer" | "vendor" | "employee")[];
  helperText?: string;
}) {
  const visible = filter
    ? parties.filter((p) => filter.includes(p.party_type as never))
    : parties;
  return (
    <TextField select size="small" label={label} value={value}
      onChange={(e) => onChange(e.target.value)} fullWidth
      sx={{ mt: 1.5 }} helperText={helperText}>
      <MenuItem value="">— none —</MenuItem>
      {visible.map((p) => (
        <MenuItem key={p.id} value={p.id}>
          <span className="font-mono text-xs mr-2">{p.code}</span>
          {p.name}
          <span className="text-[10px] text-slate-400 ml-2 dark:text-slate-500">{p.party_type}</span>
        </MenuItem>
      ))}
    </TextField>
  );
}
